import { Component, computed, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { IonicModule, LoadingController } from '@ionic/angular';
import { lastValueFrom } from 'rxjs';
import * as Sentry from '@sentry/angular';
import { HeaderComponent } from 'src/app/fleet/components/header/header.component';
import { CombinedOrdersContextService } from '../../services/combined-order.service';
import { OrderItemComponent } from './components/order-item/order-item.component';
import {
  CombinedOrderItem,
  Order,
  OrderFormValues,
  OrderStatus,
} from '../../types';
import { OrdersService } from '../../services/orders.service';
import { ToastService } from 'src/app/services/toast.service';
import { SkeletonListComponent } from 'src/app/fleet/components/skeleton-list/skeleton-list.component';
import { FileUploadService } from '../../services/file-upload.service';
import { OrderDataStoreService } from '../../services/order-data-store.service';
import { ActionSheetService } from 'src/app/services/action-sheet.service';

@Component({
  selector: 'app-summary',
  templateUrl: './summary.page.html',
  styleUrls: ['./summary.page.scss'],
  standalone: true,
  imports: [
    IonicModule,
    TranslateModule,
    CommonModule,
    HeaderComponent,
    OrderItemComponent,
    RouterModule,
    SkeletonListComponent,
  ],
})
export class SummaryPage implements OnInit {
  readonly draftOrders = this.combinedOrders.orders;
  readonly offerType = this.combinedOrders.offerType;
  readonly maxOrders = CombinedOrdersContextService.MAX_ORDERS;

  combiNumber?: string;

  createdOrders = signal<OrderFormValues[] | Order[]>([]); // TODO:orders from the backend

  orders = computed(() => {
    if (this.createdOrders().length > 0) {
      const orders = this.createdOrders();

      const calculatedOrders: CombinedOrderItem[] = orders.map(
        (order, index) => ({
          id: (index + 1).toString(),
          order: {
            ...order,
            totalAmount: order.products?.reduce(
              (acc, product) => acc + product.amount,
              0,
            ),
          },
        }),
      );

      return calculatedOrders;
    }

    return this.draftOrders();
  });

  totalAmount = computed(() =>
    this.orders().reduce(
      (acc, orderItem) => acc + (orderItem.order.totalAmount ?? 0),
      0,
    ),
  );

  ordersCountColor = computed(() =>
    this.orders().length === 1 ? 'danger' : 'secondary',
  );

  brand = this.orderDataStore.brand;

  constructor(
    private combinedOrders: CombinedOrdersContextService,
    private router: Router,
    private route: ActivatedRoute,
    private ordersService: OrdersService,
    private toastService: ToastService,
    private loadingController: LoadingController,
    private translateService: TranslateService,
    private actionSheetService: ActionSheetService,
    private orderDataStore: OrderDataStoreService,
    public fileUploadService: FileUploadService,
  ) {}

  async ngOnInit() {
    await this.populateCombinedOrder();

    if (this.combinedOrders.orders().length === 0) {
      this.router.navigate(['/fleet/orders']);
    }
  }

  async populateCombinedOrder() {
    this.combiNumber = this.route.snapshot.params['combiNumber'];

    if (!this.combiNumber) {
      return;
    }

    this.combinedOrders.clear();

    try {
      const combinedOrder = await lastValueFrom(
        this.ordersService.getCombinedOrder(this.combiNumber),
      );

      this.combinedOrders.populate(this.combiNumber, combinedOrder);
    } catch (error) {
      Sentry.captureException(error);

      this.toastService.showErrorToast(
        new Error('movacarfleet_order_edit_error_retrieving_order'),
      );
    }
  }

  cancel() {
    this.combinedOrders.clear();
  }

  async submit() {
    const loading = await this.loadingController.create({
      message: this.translateService.instant(
        'movacarfleet_orders_submitting_combined_order',
      ),
    });

    await loading.present();

    try {
      const response = await lastValueFrom(
        this.ordersService.createCombinedOrder(this.draftOrders()),
      );

      if (
        response.combiNumber &&
        this.draftOrders().some((order) => order.order.files?.length)
      ) {
        this.combinedOrders.ordersReference.set(response.combiNumber);
        await this.handleFilesUpload(response.combiNumber);
      }

      if (this.fileUploadService.allFilesUploaded()) {
        this.combinedOrders.clear();
        this.router.navigate(['/fleet/orders']);
      }
    } catch (error: any) {
      this.toastService.showErrorToast(
        new Error('movacarfleet_orders_error_submitting_combined_order'),
      );
    }

    loading.dismiss();
  }

  async handleFilesUpload(combiNumber: string) {
    if (this.combinedOrders.salesforceOrders().length === 0) {
      this.combinedOrders.salesforceOrders.set(
        await this.getRefKeysAndOrderNumbers(combiNumber),
      );
    }

    const orders = this.draftOrders();

    try {
      for (const sfOrder of this.combinedOrders.salesforceOrders()) {
        const matchingOrder = orders.find(
          (order) => order.id === sfOrder.combiNumberOrder?.toString(),
        );

        if (matchingOrder?.order.files?.length) {
          await this.fileUploadService.handleUploadFiles(
            sfOrder.referenceKey ?? null,
            matchingOrder.id,
          );
        }
      }
    } catch (error) {
      this.toastService.showErrorToast(
        new Error('movacarfleet_orders_files_error_uploading_file'),
      );
    }
  }

  async getRefKeysAndOrderNumbers(
    combiNumber: string,
  ): Promise<Pick<OrderFormValues, 'referenceKey' | 'combiNumberOrder'>[]> {
    const response = await lastValueFrom(
      this.ordersService.getCombinedOrder(combiNumber),
    );

    if (response.length === 0) {
      return [];
    }

    return response.map((order) => ({
      referenceKey: order.referenceKey,
      combiNumberOrder: order.combiNumberOrder,
    }));
  }

  async handleFilesReUpload() {
    const loading = await this.loadingController.create({
      message: this.translateService.instant(
        'movacarfleet_orders_uploading_files',
      ),
    });

    await loading.present();

    try {
      await this.handleFilesUpload(this.combinedOrders.ordersReference());

      if (this.fileUploadService.allFilesUploaded()) {
        this.combinedOrders.clear();
        this.router.navigate(['/fleet/orders']);
      }
    } catch (error) {
      this.toastService.showErrorToast(
        new Error('movacarfleet_orders_files_error_uploading_file'),
      );

      this.fileUploadService.allFilesUploaded.set(false);
    }

    await loading.dismiss();
  }

  async cancelOrders(combiNumber: string) {
    const loading = await this.loadingController.create({
      message: this.translateService.instant(
        'movacarfleet_cancel_combined_order_loading_message',
      ),
    });

    await loading.present();

    try {
      await lastValueFrom(this.ordersService.cancelCombinedOrder(combiNumber));

      this.toastService.showToast(
        'success',
        'movacarfleet_success_cancel_combined_order',
      );

      this.combinedOrders.clear();
      this.router.navigate(['/fleet/orders']);
    } catch (error) {
      this.toastService.showErrorToast(
        new Error('movacarfleet_error_cancel_combined_order'),
      );
    }

    await loading.dismiss();
  }

  async onCancelOrders(combiNumber: string) {
    const message = 'movacarfleet_order_cancel_combined_order_action_message';

    this.actionSheetService.presentActionSheet(message, () =>
      this.cancelOrders(combiNumber),
    );
  }

  onSubmit() {
    const message = 'movacarfleet_order_submit_action_message';

    this.actionSheetService.presentActionSheet(message, () => this.submit());
  }

  private isOrderType(order: Order | OrderFormValues): order is Order {
    return 'status' in order;
  }

  isCombiOrderCancelled() {
    return this.orders().every((orderItem) => {
      const order = orderItem.order;
      return this.isOrderType(order) && order.status === OrderStatus.CANCELLED;
    });
  }
}
